import { Button, Flex, Input, Text, useBoolean } from '@chakra-ui/react';
import { publishInfograph } from 'api/publishInfograph';
import { ReactComponent as CopyLinkIcon } from 'assets/icons/share_link.svg';
import { COPY_SHARE_LINK } from 'constants/mixpanel';
import { useDetectPhishing } from 'hooks/useDetectPhishing/useDetectPhishing';
import { PhishingShareMethod } from 'hooks/useDetectPhishing/useDetectPhishing.config';
import { useVenngageE1Gate } from 'hooks/useVenngageE1Gate';
import { useAppSelector } from 'modules/Editor/store';
import { selectInfographTitle } from 'modules/Editor/store/infographSelector';
import {
  ClickEventTracker,
  EventPropsBool,
  ShareMethod,
} from 'modules/common/components/ClickEventHandler/ClickEventWrapper';
import { useToast } from 'modules/common/components/Toast';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';

import { HandleClickUpgrade } from './DownloadModal/DownloadModal.types';
import { UpgradeLock } from './DownloadModal/OptionTitle';

interface PublishPanelProps {
  handleClickUpgrade?: HandleClickUpgrade;
}

const PublishPanel = ({ handleClickUpgrade }: PublishPanelProps) => {
  const { id: infographId } = useParams();
  const { t } = useTranslation('common_navbar');
  const { detectPhishing, phishingToast } = useDetectPhishing();
  const { venngageE2 } = useVenngageE1Gate();
  const [isLoading, setLoading] = useBoolean();

  const [published, setPublished] = useBoolean(venngageE2?.isPublished ?? false);
  const infographTitle = useAppSelector(selectInfographTitle);

  const handleClickPublish = async () => {
    try {
      if (infographId) {
        setLoading.on();
        const phishing = await detectPhishing(PhishingShareMethod.Publish);
        setLoading.off();

        if (phishing?.phishingDetected) {
          phishingToast({ description: t('phishing.message'), title: t('phishing.title') });
          return;
        }
        await publishInfograph(infographId, infographTitle);
        setPublished.on();

        if ('VenngageE2' in window) {
          VenngageE2.isPublished = true;
        }
      } else {
        throw Error('InfographId not accessible, publish failed');
      }
    } catch (err) {
      console.error(err);
    }
  };

  const toast = useToast();

  const showCopySuccessToast = () => {
    toast({
      title: 'Link copied!',
    });
  };

  const showCopyFailedToast = (description?: string) => {
    const descriptionObj = description ? { description } : null;
    toast({
      title: 'Failed to copy link',
      status: 'error',
      ...descriptionObj,
    });
  };

  const handleClickCopyLink = () => {
    const shareLinkId = venngageE2?.privateLinkId;
    if (!!shareLinkId) {
      const plPageUrl = `${window.location.protocol}//${window.location.host}/pl/${shareLinkId}`;
      detectPhishing(PhishingShareMethod.CopyLink);
      navigator.clipboard.writeText(plPageUrl).then(
        () => {
          showCopySuccessToast();
        },
        () => {
          showCopyFailedToast();
        },
      );
    } else {
      showCopyFailedToast('share link is not available');
    }
  };

  return (
    <Flex flexDir='column' rowGap='4' color='font.500' mt={2}>
      <Text fontWeight='semibold' fontSize='sm' lineHeight='1.25'>
        {t('modals.publish-panel.publish-to-web')}
      </Text>
      <Text fontSize='xs' lineHeight='1.43'>
        {t('modals.publish-panel.access-to-design-text')}
      </Text>
      {published && (
        <>
          <Flex flexDir='column' rowGap='2'>
            <Text fontWeight='semibold' fontSize='sm'>
              {t('modals.publish-panel.public-title')}
            </Text>
            <Input
              size='sm'
              height='7'
              color='font.300'
              border='1px solid'
              borderColor='gray.200'
              borderRadius='base'
              noOfLines={1}
              pointerEvents='none'
              placeholder={infographTitle}
            />
          </Flex>
          <Flex flexDir='column' rowGap='2'>
            <Text fontWeight='semibold' fontSize='sm'>
              {t('modals.publish-panel.public-link')}
            </Text>
            <ClickEventTracker
              eventProps={{
                name: COPY_SHARE_LINK.eventName,
                'User Plan': 'free',
                design_id: infographId,
                share_method: ShareMethod.Link,
                is_design_owner: EventPropsBool.true,
              }}
            >
              <Button
                onClick={handleClickCopyLink}
                variant='action'
                h='8'
                px='3'
                py='1.5'
                leftIcon={<CopyLinkIcon style={{ marginRight: 1 }} />}
              >
                {t('modals.share-panel.copy-link')}
              </Button>
            </ClickEventTracker>
          </Flex>
        </>
      )}
      {!published && (
        <Button
          variant='outline'
          borderColor='gray.200'
          w='full'
          h='8'
          fontWeight='medium'
          fontSize='sm'
          onClick={handleClickPublish}
          disabled={isLoading}
        >
          {t('modals.publish-panel.title-publish')}
        </Button>
      )}
      <UpgradeLock
        upgradeText={t('modals.publish-panel.upgrade')}
        handleClickUpgrade={handleClickUpgrade}
        postText={t('modals.publish-panel.share-or-download')}
      />
    </Flex>
  );
};

export default PublishPanel;
